import {
    REWARDS_SPENDING_RULES_REWARD_TYPES,
    REWARDS_SPENDING_RULES_TYPES,
} from "@apps/rewards/modules/SpendingRules/constants";
import type { RewardsSpendingRulesEntity } from "@apps/rewards/modules/SpendingRules/models/RewardsSpendingRulesEntities";

import type {
    SpendingRulesRewardsDTO,
    SpendingRulesSdkResultDTO,
} from "../types";

const { amountDiscount, percentageDiscount } = REWARDS_SPENDING_RULES_TYPES;
const {
    fixedAmountDiscount,
    flexibleAmountDiscount,
    fixedPercentageDiscount,
    flexiblePercentageDiscount,
    giftCard,
    shippingDiscount,
    productDiscount,
} = REWARDS_SPENDING_RULES_REWARD_TYPES;

const getSpendingRulesRewardsDto = (
    rewards: RewardsSpendingRulesEntity["rewards"]
): SpendingRulesRewardsDTO => {
    return rewards.map((reward) => {
        switch (reward.rewardType) {
            case fixedAmountDiscount:
                return {
                    templateId: reward.templateId,
                    rewardType: reward.rewardType,
                    amount: reward.amount,
                };

            case flexibleAmountDiscount:
                return {
                    templateId: reward.templateId,
                    rewardType: reward.rewardType,
                    everyX: reward.everyX,
                    amountY: reward.amountY,
                };

            case fixedPercentageDiscount:
                return {
                    templateId: reward.templateId,
                    rewardType: reward.rewardType,
                    percents: reward.percents,
                };

            case flexiblePercentageDiscount:
                return {
                    templateId: reward.templateId,
                    rewardType: reward.rewardType,
                    everyX: reward.everyX,
                    percentsY: reward.percentsY,
                };

            case giftCard:
                return {
                    templateId: reward.templateId,
                    rewardType: reward.rewardType,
                    initialValue: reward.initialValue,
                };

            case shippingDiscount:
                return {
                    templateId: reward.templateId,
                    rewardType: reward.rewardType,
                    minOrderPrice: reward.minOrderPrice,
                };

            case productDiscount:
                return {
                    templateId: reward.templateId,
                    rewardType: reward.rewardType,
                    productId: reward.productId,
                    variantId: reward.variantId,
                    percents: reward.percents,
                };
        }
    });
};

export const getSpendingRulesDto = (
    items: Array<RewardsSpendingRulesEntity>
): Array<SpendingRulesSdkResultDTO> => {
    return items.map(
        ({ metadata: { products, icons, variants }, rule, rewards }) => {
            const baseRule = {
                ruleId: rule.ruleId,
                title: rule.title,
                icon: rule.icon,
                pointsPrice: rule.pointsPrice,
                ruleType: rule.ruleType,
            };

            const extendedRule =
                rule.ruleType === amountDiscount ||
                rule.ruleType === percentageDiscount
                    ? {
                          ...baseRule,
                          maxSpendablePoints: rule.maxSpendablePoints,
                          minSpendablePoints: rule.minSpendablePoints,
                      }
                    : baseRule;

            return {
                metadata: {
                    icons: icons.map((icon) => ({
                        method: icon.method,
                        w: icon.w,
                        h: icon.h,
                        url: icon.url,
                        label: icon.label,
                    })),
                    products: products.map((product) => ({
                        id: product.id,
                    })),
                    variants: variants.map((variant) => ({
                        id: variant.id,
                    })),
                },
                rule: extendedRule,
                rewards: getSpendingRulesRewardsDto(rewards),
            };
        }
    );
};
